import { cardPeriodForDate } from "./finance";
import { isExpectedInstallment, bankPostingLabel, movementActivityDate } from "./bank-posting";
import { cardMovementStatus } from "./card-status";
import type { BankCredit, CardPaymentTimingAdjustment, Movement } from "./types";
import { paymentTimingForCut } from "./card-payment-timing";

export type CardStatementPeriod = "actual" | "anterior";

export interface CardStatementMovement {
  id: string;
  fecha: string;
  descripcion: string;
  categoria?: string;
  gasto: number;
  reembolso: number;
  reembolsoPendiente: boolean;
  cubierto: number;
  pendiente: number;
  estado: string;
  situacionBanco?: string;
  parcialidadPosterior?: boolean;
  confirmadaBanco?: boolean;
  fechaPromesa?: string;
  comprobante?: string;
}

export interface CardStatementSummary extends Record<string, unknown> {
  ok: true;
  tarjeta: string;
  periodo?: string;
  puedeVerAnterior?: boolean;
  actualizado?: string;
  sinDatos?: boolean;
  mensaje?: string;
  totales?: {
    totalBruto: number;
    cargosRegistrados: number;
    cargosProgramados: number;
    bonificaciones: number;
    ajusteEntreCortes: number;
    netoRegistrado: number;
    gastoReal: number;
    cubierto: number;
    pendiente: number;
    reembolsoTotal: number;
    reembolsoPendiente: number;
    noCubiertos: number;
    porcentajeCubierto: number;
    movimientos: number;
  };
  movimientos?: CardStatementMovement[];
}

const pesos = (cents: number): number => Math.round(cents) / 100;

function periodEnd(period: string): string {
  return period.match(/al\s+(\d{4}-\d{2}-\d{2})$/)?.[1] ?? period;
}

/**
 * Prepara el mismo documento derivado usando únicamente movimientos que ya
 * están disponibles en el dispositivo. No escribe saldos ni colecciones.
 */
export function buildCardStatementSummary(
  movements: Movement[],
  card: string,
  requested: CardStatementPeriod,
  cutDay: number,
  today: string,
  now = new Date(),
  credits: BankCredit[] = [],
  timingAdjustments: CardPaymentTimingAdjustment[] = [],
): CardStatementSummary {
  const validCutDay = Number.isInteger(cutDay) && cutDay >= 1 && cutDay <= 31 ? cutDay : 16;
  const currentPeriod = cardPeriodForDate(today, validCutDay);
  if (!currentPeriod) {
    return { ok: true, tarjeta: card, sinDatos: true, mensaje: "El día de corte no es válido." };
  }

  const candidates = movements
    .filter((movement) => movement.kind === "expense" && movement.paymentMethod === card)
    .map((movement) => ({
      movement,
      period: movement.cardPeriod
        ?? cardPeriodForDate(String(movement.spentOn ?? movement.createdAt).slice(0, 10), validCutDay),
    }))
    .filter((item): item is { movement: Movement; period: string } => Boolean(item.period));
  const periods = [...new Set(candidates.map((item) => item.period))]
    .sort((a, b) => periodEnd(a).localeCompare(periodEnd(b)));
  const currentStart = currentPeriod.slice(0, 10);
  const previousPeriod = periods.filter((period) => periodEnd(period) < currentStart).at(-1);
  const selectedPeriod = requested === "anterior" ? previousPeriod : currentPeriod;

  if (!selectedPeriod) {
    return {
      ok: true,
      tarjeta: card,
      sinDatos: true,
      mensaje: "No hay un periodo anterior registrado para esta tarjeta.",
    };
  }

  const selected = candidates
    .filter((item) => item.period === selectedPeriod)
    .map((item) => item.movement)
    .sort((a, b) => String(b.spentOn ?? b.createdAt).localeCompare(String(a.spentOn ?? a.createdAt)));
  const totalsCents = selected.reduce((total, movement) => {
    const billed = Math.max(0, movement.billedAmountCents ?? movement.amountCents);
    const reimbursement = Math.max(0, movement.reimbursementCents ?? 0);
    const covered = Math.min(billed, Math.max(0, movement.coveredAmountCents ?? movement.amountCents));
    const pending = Math.max(0, movement.pendingAmountCents ?? billed - covered);
    total.totalBruto += billed;
    total.gastoReal += Math.max(0, billed - reimbursement);
    total.cubierto += covered;
    total.pendiente += pending;
    total.reembolsoTotal += reimbursement;
    total.reembolsoPendiente += movement.reimbursementReceived ? 0 : reimbursement;
    if (pending > 0 || (reimbursement > 0 && !movement.reimbursementReceived)) total.noCubiertos += 1;
    return total;
  }, { totalBruto: 0, gastoReal: 0, cubierto: 0, pendiente: 0, reembolsoTotal: 0, reembolsoPendiente: 0, noCubiertos: 0 });

  const statementMovements = selected.map((movement): CardStatementMovement => {
    const billed = Math.max(0, movement.billedAmountCents ?? movement.amountCents);
    const covered = Math.min(billed, Math.max(0, movement.coveredAmountCents ?? movement.amountCents));
    const pending = Math.max(0, movement.pendingAmountCents ?? billed - covered);
    const reimbursement = Math.max(0, movement.reimbursementCents ?? 0);
    return {
      id: movement.id,
      fecha: movementActivityDate(movement),
      descripcion: movement.description ?? movement.merchant,
      categoria: movement.category,
      gasto: pesos(billed),
      reembolso: pesos(reimbursement),
      reembolsoPendiente: reimbursement > 0 && !movement.reimbursementReceived,
      cubierto: pesos(covered),
      pendiente: pesos(pending),
      estado: cardMovementStatus(movement, today),
      situacionBanco: bankPostingLabel(movement, today),
      parcialidadPosterior: Boolean(movement.installmentPurchaseId && movement.installmentNumber !== 1 && movement.cardPaymentStatus !== "paid" && !movement.historicalSettlement),
      confirmadaBanco: movement.bankPostingState === "confirmed",
      fechaPromesa: movement.promiseDate,
      comprobante: movement.receiptUrl,
    };
  });

  const bankCredits = credits.filter(item=>item.card===card && item.period===selectedPeriod);
  const creditCents = bankCredits.reduce((n,item)=>n+item.amountCents,0);
  const scheduledCents = selected.filter(item=>isExpectedInstallment(item,today)).reduce((n,item)=>n+(item.billedAmountCents??item.amountCents),0);
  // Toda compra capturada cuenta por igual; sólo se separan parcialidades futuras.
  const registeredCents = totalsCents.totalBruto-scheduledCents;
  for (const credit of bankCredits) statementMovements.push({id:credit.id,fecha:credit.postedOn,descripcion:`Bonificación bancaria · ${credit.description}`,categoria:"Bonificación bancaria",gasto:-pesos(credit.amountCents),reembolso:0,reembolsoPendiente:false,cubierto:0,pendiente:0,estado:"Crédito del banco · no es ingreso en Nu"});
  const paymentTimingCents = paymentTimingForCut(timingAdjustments, card, selectedPeriod);
  for (const adjustment of timingAdjustments.filter((item) => item.card === card && (item.paymentPeriod === selectedPeriod || item.sourcePeriod === selectedPeriod))) {
    const isPaymentPeriod = adjustment.paymentPeriod === selectedPeriod;
    statementMovements.push({
      id: adjustment.id,
      fecha: adjustment.createdAt.slice(0, 10),
      descripcion: isPaymentPeriod ? "Bonificación aplicada al saldo anterior" : "Bonificación ya aplicada al pago anterior",
      categoria: "Ajuste entre cortes",
      gasto: pesos(isPaymentPeriod ? -adjustment.amountCents : adjustment.amountCents),
      reembolso: 0,
      reembolsoPendiente: false,
      cubierto: 0,
      pendiente: 0,
      estado: "Ajuste de pago · no es una compra ni dinero nuevo",
    });
  }

  return {
    ok: true,
    tarjeta: card,
    periodo: selectedPeriod,
    puedeVerAnterior: Boolean(previousPeriod),
    actualizado: new Intl.DateTimeFormat("es-MX", { dateStyle: "short", timeStyle: "short" }).format(now),
    totales: {
      totalBruto: pesos(totalsCents.totalBruto),
      cargosRegistrados:pesos(registeredCents),cargosProgramados:pesos(scheduledCents),bonificaciones:pesos(creditCents),ajusteEntreCortes:pesos(paymentTimingCents),netoRegistrado:pesos(registeredCents-creditCents+paymentTimingCents),
      gastoReal: pesos(totalsCents.gastoReal),
      cubierto: pesos(totalsCents.cubierto),
      pendiente: pesos(totalsCents.pendiente),
      reembolsoTotal: pesos(totalsCents.reembolsoTotal),
      reembolsoPendiente: pesos(totalsCents.reembolsoPendiente),
      noCubiertos: totalsCents.noCubiertos,
      porcentajeCubierto: totalsCents.totalBruto
        ? Math.round(totalsCents.cubierto / totalsCents.totalBruto * 1000) / 10
        : 0,
      movimientos: statementMovements.length,
    },
    movimientos: statementMovements,
  };
}
